/**
 * One set of stat formatters for every live figure on the marketing site.
 *
 * WHY THIS EXISTS. Five components each grew their own `compact()` / `num()` / `fmtCount()` with
 * slightly different thresholds, and one of them printed 1,440,377 in full — seven digits and two
 * commas in a tile sized for three characters, which wraps, shrinks the label and makes a good
 * number look like a database dump. Divergent rounding across tiles is worse than any single choice:
 * two stats on the same row disagreeing about whether 45,800 is "45.8k" or "46k" reads as sloppiness.
 *
 * THE RULE: a stat is a headline, not a receipt. It gets at most four characters plus a unit, always
 * steps up to the next unit rather than growing wider, and never carries thousands separators. If a
 * reader needs the exact figure they are on the wrong page — this is the shop window, and the
 * operations dashboard is where precision lives.
 *
 * Every function returns an em dash for null/undefined/NaN/≤0 rather than "0" or "NaN". A missing
 * figure and a genuine zero are different claims, and a component that cannot reach its endpoint
 * must not silently assert that the answer is nothing.
 */

/** Counts: 1.4M, 45.8k, 132. Never "1,440,377". */
export function fmtCount(n?: number | null): string {
  if (n == null || !isFinite(n) || n <= 0) return "—";
  if (n >= 1_000_000_000) return `${trim(n / 1_000_000_000)}B`;
  if (n >= 1_000_000) return `${trim(n / 1_000_000)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1_000) return `${trim(n / 1000)}k`;
  return String(Math.round(n));
}

/** Bytes: 22.2 KB, 4.8 GB, 1.2 TB. */
export function fmtBytes(n?: number | null): string {
  if (n == null || !isFinite(n) || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 100 ? trim(v) : Math.round(v)} ${units[i]}`;
}

/**
 * Durations, stepping up rather than growing: 84 ms, 711 ms, 1.4 s.
 * Sub-millisecond reads as "<1 ms" — a cache hit rounding to "0 ms" looks broken rather than fast,
 * which is the opposite of the point being made.
 */
export function fmtMs(n?: number | null): string {
  if (n == null || !isFinite(n) || n < 0) return "—";
  if (n === 0) return "—";
  if (n < 1) return "<1 ms";
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${trim(n / 1000)} s`;
}

/** Percentages, whole numbers. Finer than that is false precision on a figure that moves hourly. */
export function fmtPct(n?: number | null): string {
  if (n == null || !isFinite(n) || n < 0) return "—";
  return `${Math.round(n)}%`;
}

/** A share expressed as part/whole, e.g. junk caught out of pages judged. */
export function fmtShare(part?: number | null, whole?: number | null): string {
  if (!part || !whole || whole <= 0) return "—";
  return `${Math.round((part / whole) * 100)}%`;
}

/** Drops a trailing ".0" so 4.0M renders as 4M — a dangling zero is a wasted character. */
function trim(v: number): string {
  const s = v.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}
